import { Controller } from "@hotwired/stimulus"

// The in-canvas comment thread's actions. The card holds one section per top-level
// comment on the element (TMS-1616), each with its own footer bar (Resolve / Reply /
// Dismiss, with Resolve↔Reopen swapping by status) and kebab (Delete) — every action
// works on the section it was pressed in.
//
// The thread card is appended to <body> (see course-review-canvas), so — exactly
// like the composer — it carries its own controller; a data-action here could not
// resolve against the canvas controller further up the tree. Every action hits the
// same comments endpoint the Review Activity panel uses and announces the change
// through `course-review:changed`, which repaints the markers and closes this card.
export default class extends Controller {
  static values = { commentsUrl: String, context: Object }

  connect() {
    this.onDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener("click", this.onDocumentClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick)
  }

  toggleMenu(event) {
    event.stopPropagation()
    const kebab = event.currentTarget
    const menu = this.sectionFor(kebab)?.querySelector(".course-review-thread__menu")
    if (!menu) return

    const opening = menu.hidden
    this.closeMenus()
    menu.hidden = !opening
    kebab.setAttribute("aria-expanded", opening ? "true" : "false")
  }

  // A click anywhere outside a toolbar closes the menus — they are popovers, not pinned
  // panels.
  handleDocumentClick(event) {
    if (event.target.closest(".course-review-thread__toolbar")) return

    this.closeMenus()
  }

  closeMenus() {
    this.element.querySelectorAll(".course-review-thread__menu").forEach((menu) => { menu.hidden = true })
    this.element.querySelectorAll(".course-review-thread__kebab").forEach((kebab) => {
      kebab.setAttribute("aria-expanded", "false")
    })
  }

  // Swap the action bars for the reply composer: move the composer the canvas hosted
  // (collapsed) in this card under the pressed thread, point it at that thread's
  // comment, and reveal + focus it. Every section's bar and Delete kebab hide while the
  // reply is open, so the target can't change — or vanish — under a half-written draft.
  focusReply(event) {
    const section = this.sectionFor(event.currentTarget)
    const composer = this.element.querySelector(".course-review-composer")
    if (!section || !composer) return

    // A stale Resolve/Dismiss error would otherwise sit above the composer it has
    // nothing to do with.
    this.clearErrors()
    this.toggleThreadActions(false)
    section.querySelector("[data-thread-footer]").before(composer)
    composer.dataset.courseReviewComposerParentIdValue = section.dataset.commentId
    composer.classList.remove("course-review-composer--collapsed")
    composer.querySelector("[data-course-review-composer-target='body']")?.focus({ preventScroll: true })
    this.revealComposer()
  }

  // The composer sits inside the scrolling thread list, so focusing its text box only
  // scrolls that box into view and leaves Cancel/Comment below the fold. Scroll the whole
  // composer in instead — on open, and again as the draft grows while the reply is typed.
  revealComposer() {
    const composer = this.element.querySelector(".course-review-composer:not(.course-review-composer--collapsed)")
    composer?.scrollIntoView({ block: "nearest" })
  }

  // Cancel/Escape from the hosted reply composer (which has already cleared its draft):
  // tuck it away and bring the Resolve/Reply/Dismiss bars and Delete kebabs back.
  cancelReply() {
    this.clearErrors()
    this.toggleThreadActions(true)
    this.element.querySelector(".course-review-composer")?.classList.add("course-review-composer--collapsed")
  }

  // Any change to a thread closes the card (see course-review:changed), so while a reply
  // is open every action that changes a thread — not just the footer's — must be out of
  // reach, or the draft is lost with it.
  toggleThreadActions(visible) {
    this.closeMenus()
    this.element.querySelectorAll("[data-thread-footer], [data-thread-toolbar]").forEach((bar) => { bar.hidden = !visible })
  }

  resolveComment(event) {
    this.setStatus(event, "resolved")
  }

  dismissComment(event) {
    this.setStatus(event, "dismissed")
  }

  reopenComment(event) {
    this.setStatus(event, "pending")
  }

  setStatus(event, status) {
    this.changeComment(this.sectionFor(event.currentTarget), { method: "PATCH", comment: { status } })
  }

  deleteComment(event) {
    this.changeComment(this.sectionFor(event.currentTarget), { method: "DELETE" })
  }

  sectionFor(element) {
    return element.closest("[data-thread-section]")
  }

  // Mirrors course-review-activity#changeComment: the row, the panel and the marker
  // are all server-rendered, so a successful change asks for a re-render rather than
  // editing any of them here.
  async changeComment(section, { method, comment = null }) {
    const commentId = section?.dataset.commentId
    if (!commentId || !this.hasCommentsUrlValue) return

    this.closeMenus()
    this.clearErrors()

    try {
      const response = await fetch(`${this.commentsUrlValue}/${commentId}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ ...this.contextValue, ...(comment ? { comment } : {}) })
      })

      const payload = await response.json()
      if (!response.ok || !payload.success) {
        throw Object.assign(new Error("comment change failed"), { serverErrors: payload.errors })
      }

      window.dispatchEvent(new CustomEvent("course-review:changed"))
    } catch (error) {
      // The markers stay as they are and the reviewer can retry, but the card says
      // why the change was refused instead of silently ignoring the click.
      const message = Array.isArray(error.serverErrors) ? error.serverErrors.filter(Boolean).join(" ") : ""
      this.showError(section, message || "Couldn't save your change. Try again.")
    }
  }

  // Only the text changes: the role="alert" region stays rendered so screen readers
  // announce the message (the CSS collapses it while empty).
  showError(section, message) {
    const error = section.querySelector("[data-thread-error]")
    if (error) error.textContent = message
  }

  clearErrors() {
    this.element.querySelectorAll("[data-thread-error]").forEach((error) => { error.textContent = "" })
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
